import React, { useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Box, Button, Link, Alert, AlertTitle, AlertDescription, AlertIcon } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { countries } from 'data';
import { PageHeader } from 'shared-components';
import * as uiActions from '../../../../store/ui/actions';

import {
  IPersonalInfo,
  IFinance,
  OrganisationDocsMap,
  CustomRequestMap,
  EducationDocsMap,
  BirthCertificateDocs,
  FamilySponsorshipDocMap,
  DepositDoc,
  ScholarshipDocsMap,
  BirthCertificateDoc,
  BankLetterDoc,
  ParentLetterDoc,
  IDocument,
  BankStatementDoc,
  Organisation,
  OrganisationDoc,
  Scholarship,
  ScholarshipDoc,
  Education,
  EducationDoc,
  CustomRequest,
  EvidenceOfIncomeDocs,
  IEvidenceOfIncomeDoc
} from '../../../../types';
import EvidenceOfIncomeSection from '../../../molecules/financeDocumentSection/evidenceOfIncome';
import FamilySection from '../../../molecules/financeDocumentSection/family';
import EducationSponsor from '../../../molecules/financeDocumentSection/education';
import ScholarshipSponsor from '../../../molecules/financeDocumentSection/scholarship';
import GovernmentOrCompany from '../../../molecules/financeDocumentSection/governmentOrCompany';
import CustomRequestDocument from '../../../molecules/financeDocumentSection/customRequest';
import Deposit from '../../../molecules/financeDocumentSection/deposit';
import { formatCurrency } from '../../../../utilities/formatting';
import BankStatementModal from '../../modals/financeDocs/bankStatement';
import BankLetterModal from '../../modals/financeDocs/personalBankLetter';
import ParentLetterModal from '../../modals/financeDocs/parentLetter';
import CurriculumVitaeModal from '../../modals/financeDocs/curriculumVitae';
import EmploymentReferenceModal from '../../modals/financeDocs/employmentReference';
import RecentEmploymentContractModal from '../../modals/financeDocs/recentEmploymentContract';
import RecentPayslipModal from '../../modals/financeDocs/recentPayslip';
import BankStatementTranslationModal from '../../modals/financeDocs/bankTranslation';
import OrganisationSponsorModal from '../../modals/financeDocs/organisationSponsor';
import ScholarshipSponsorModal from '../../modals/financeDocs/scholarshipSponsor';
import EducationSponsorModal from '../../modals/financeDocs/educationSponsor';
import DepositModal from '../../modals/financeDocs/deposit';
import BirthCertificateModal from '../../modals/financeDocs/birthCertificate';
import CustomRequestModal from '../../modals/financeDocs/customRequestDoc';
import AwaitingDepositModal from '../../modals/financeDocs/awaitingDeposit';
import BirthCertificateTranslationModal from '../../modals/financeDocs/birthCertificateTranslation';
import VerificationBankLetterModal from '../../modals/financeDocs/verificationBankLetter';
import SecondaryBankStatementModal from '../../modals/financeDocs/secondaryBankStatement';

import InvalidFundingModal from '../../modals/financeDocs/invalidFunding';
import { getOrgSponsorType } from '../../../organisms/finance/OrganisationSponsor/utils';
import { sponsorFundLabel } from '../../../../utilities/sponsors';
import {
  bankStatementName,
  bankStatementTransName,
  bankLetterName,
  getLeadSponsorByKey,
  findBirthCertSponsor
} from '../../../../utilities/documents';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { DepositDocHash, FamilySponsorship, ICore, OtherSponsorship } from 'types';
import { getInvalidFundingSources } from '../../../../utilities/getInvalidFundingSources';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';
import { LOCKED_SECTION_SUMMARY } from '../../../../utilities/constants';
import PageSubtext from '../../../atoms/PageSubtext';
import SectionHeader from '../../../atoms/SectionHeader';
import { titleAppend } from '../../../../utilities/head';
import { getStudentFinDocs } from '../../../../store/student/actions';

import { createNavigateToAction } from '../../../../middleware/actions';
import {
  addRequiredDocument,
  saveMetaDataForNoDoc,
  toggleAcknowledge
} from '../../../../store/student/financeDocuments/actions';
import { hasAnyMatchingFinDocs } from '../../../../utilities/documents';
import FileUploadLocal from '../../modals/fileUpload/FileUploadLocal';

export interface IInvalidFundingSource {
  label: string;
  contributionAmount: number;
  fundingType: string;
  isBankAccepted: boolean;
}

const FinanceDocumentDashboard = () => {
  const totalFundingToShow =
    useSelector((state: ApplicationState) => state.student.financials.fundingSummary?.totalToBeShown) ?? 0;

  const documents = useSelector((state: ApplicationState) => state.student.financeDocuments);
  const summary = useSelector((state: ApplicationState) => state.student.summary);
  const hasRequiredDocs = hasAnyMatchingFinDocs(documents, 'required');
  const hasInfoOnlyDocs = hasAnyMatchingFinDocs(documents, 'infoOnly');

  const title = useSelector((state: ApplicationState) => state.config.sections.FinanceDocuments.summaryLabel);
  const summaryText = useSelector((state: ApplicationState) => state.config.sections.FinanceDocuments.summary);
  const courseFeeDepositReqd = useSelector((state: ApplicationState) => state.student.financials.courseFeeDepositReqd);
  const openModal = useSelector((state: ApplicationState) => state.ui.openModal);
  const financials = useSelector((state: ApplicationState) => state.student.financials);
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);
  const core = useSelector((state: ApplicationState) => state.student.core);
  const depositInstructionLink = useSelector((state: ApplicationState) => state.config.depositInstructionLink);

  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const { offerId, intakeId } = offer;
  const config = useSelector(({ config }: ApplicationState) => config, shallowEqual);

  const isSectionLocked = useIsSectionLocked(SectionId.FINANCE_DOC, offer.status);
  const isCASChecklistSectionLocked = useIsSectionLocked(SectionId.CHECKLIST, offer.status);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getStudentFinDocs());
  }, [dispatch]);

  const onResume = (url: string) => {
    dispatch(createNavigateToAction(url));
  };
  const openDocumentModal = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    infoOnly: boolean,
    modalTitle?: string
  ) => {
    dispatch(uiActions.openFinanceDocumentModal(sponsorType, sponsorKey, docType, docKey, infoOnly, modalTitle));
  };
  const closeModal = () => {
    dispatch(uiActions.closeModal());
  };
  const openUploadFinanceModal = (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => {
    dispatch(
      uiActions.openUploadFinanceModal(sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metadata)
    );
  };
  const onSaveMetaDataForNoDoc = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    metadata?: any
  ) => {
    dispatch(saveMetaDataForNoDoc(sponsorType, sponsorKey, docType, docKey, metadata));
  };
  const onAddRequiredDocument = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    newDocType: string,
    metadata?: any
  ) => {
    dispatch(addRequiredDocument(sponsorType, sponsorKey, docType, docKey, newDocType, metadata));
  };
  const onAcknowledgeToggle = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string,
    status: string,
    infoOnlyRequirementsOption?: string,
    metadata?: any
  ) =>
    dispatch(toggleAcknowledge(sponsorType, sponsorKey, docType, docKey, status, infoOnlyRequirementsOption, metadata));

  const getInvalidFunding = () => {
    const [invalidFamilySponsorship, invalidOtherSponsorship] = getInvalidFundingSources(financials);
    const invalidFundingSources: IInvalidFundingSource[] = [];

    Object.values(invalidFamilySponsorship ?? {}).forEach((sponsor: FamilySponsorship) => {
      invalidFundingSources.push({
        label: sponsor.accountType,
        contributionAmount: sponsor.totalAmount || 0,
        fundingType: bankStatementName(sponsor.sponsorType),
        isBankAccepted: true
      });
    });

    Object.values(invalidOtherSponsorship ?? {}).forEach((sponsor: OtherSponsorship) => {
      invalidFundingSources.push({
        label: sponsor.otherName,
        contributionAmount: sponsor.sponsorshipAmount || 0,
        fundingType: 'Other',
        isBankAccepted: true
      });
    });

    let invalidFundingSection = null;
    if (invalidFundingSources.length) {
      invalidFundingSection = (
        <Alert status="error" data-selector="invalid-funding" mb={6}>
          <AlertIcon />

          <Box>
            <AlertTitle>Invalid Funding Sources</AlertTitle>

            <AlertDescription>
              The following funding sources may be <b>invalid</b> and cannot be factored into the funding calculator.
            </AlertDescription>

            {invalidFundingSources.map((source: IInvalidFundingSource, index: number) => {
              return (
                <Box key={`invalid-fund-${index}`}>
                  <Box display="grid" gridTemplateColumns="1fr 0.25fr auto" gap={3} mt={3}>
                    <Box fontWeight="bold" wordBreak="break-word">
                      {source.label}
                    </Box>

                    <Box>£{formatCurrency(source.contributionAmount)}</Box>

                    <Link as={RouterLink} to={`/${intakeId}/${offerId}/finances/step-1`}>
                      Edit
                    </Link>
                  </Box>

                  <Button
                    onClick={() => openDocumentModal('invalidFunding', null, null, null, false, source.label)}
                    mt={3}
                  >
                    How to fix
                  </Button>
                </Box>
              );
            })}
          </Box>
        </Alert>
      );
    }
    return invalidFundingSection;
  };

  function appendQueryParams(url, params) {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(params);

    // Append each query parameter to the URL
    searchParams.forEach((value, key) => {
      urlObj.searchParams.append(key, value);
    });

    return urlObj.toString();
  }

  function getTransferMateQueryParams(
    personalInfo: IPersonalInfo,
    finance: IFinance,
    core: ICore
  ): Map<string, string> {
    const queryParams = new Map<string, string>();
    if (finance.courseFeeDeposit) queryParams.set('__tuition_fees_price', finance.courseFeeDeposit.toString());
    if (personalInfo.studentNo) queryParams.set('cu_id_number', personalInfo.studentNo);
    if (personalInfo.givenName) queryParams.set('first_name', personalInfo.givenName);
    if (personalInfo.surname) queryParams.set('last_name', personalInfo.surname);
    if (core.email) queryParams.set('email', core.email);
    if (personalInfo.dob) {
      const [year, month, day] = personalInfo.dob.split('-');
      queryParams.set('__date_of_birth_day', day);
      queryParams.set('__date_of_birth_month', month);
      queryParams.set('__date_of_birth_year', year);
    }
    if (personalInfo.city) queryParams.set('suburban_city', personalInfo.city);
    if (personalInfo.country) {
      const country = countries.find(c => c.countryName === personalInfo.country);
      if (country.transferMate) queryParams.set('country', country.transferMate.toString());
    }
    if (personalInfo.homeAddress) queryParams.set('home_address', personalInfo.homeAddress);

    return queryParams;
  }

  const getDepositRequest = () => {
    if (!depositInstructionLink || courseFeeDepositReqd !== 'No') {
      return null;
    }
    let processedDepositLink = depositInstructionLink;

    if (processedDepositLink?.includes('.transfermate')) {
      let params = getTransferMateQueryParams(personalInfo, financials, core);
      processedDepositLink = appendQueryParams(processedDepositLink, params);
    }

    const depositLink = processedDepositLink?.includes('.transfermate') ? (
      <Link href={processedDepositLink} isExternal>
        Pay Now
      </Link>
    ) : (
      <Button onClick={() => openDocumentModal('awaitingDeposit', null, null, null, false, 'Deposit')} mt={3}>
        How to Fix
      </Button>
    );

    return (
      <Alert status="warning" data-selector="unpaid-deposit" mb={6}>
        <AlertIcon />

        <Box>
          <AlertTitle>Unpaid deposit</AlertTitle>

          <AlertDescription>
            To accept your offer you must <b>pay a deposit</b> to the university as soon as possible.
          </AlertDescription>

          <Box key="deposit-request">
            <Box display="grid" gridTemplateColumns="1fr auto" gap={3} mt={3}>
              <Box>Deposit (to be paid)</Box>

              <Link as={RouterLink} to={`/${intakeId}/${offerId}/finances/step-1`}>
                Edit
              </Link>
            </Box>
            {depositLink}
          </Box>
        </Box>
      </Alert>
    );
  };

  const sponsorWithBirthCert = findBirthCertSponsor(
    financials.familySponsorship ?? {},
    financials.educationSponsorship ?? {}
  );

  const renderDocs = (infoOnly = false) => {
    return Object.keys(documents)
      .sort()
      .map((key: any) => {
        if (
          ['isSectionStarted', 'isSectionComplete', 'other', 'documentsRequired', 'documentsCompleted'].includes(key)
        ) {
          return null;
        }

        switch (key) {
          case 'educationSponsorship': {
            const educationSponsorMap = documents[key] as EducationDocsMap;

            return Object.entries(educationSponsorMap).map(([eduKey, educationSponsor]) => {
              const eduSponsorFinance = financials.educationSponsorship?.[eduKey];
              if (!eduSponsorFinance) {
                return null;
              }

              let birthCertificate: BirthCertificateDocs | undefined = undefined;
              if (sponsorWithBirthCert === eduKey) {
                birthCertificate = documents.birthCertificate;
              }

              const financeAmount = eduSponsorFinance.sponsorshipAmount;
              return (
                <EducationSponsor
                  infoOnly={infoOnly}
                  key={`${eduKey}`}
                  id={`${eduKey}`}
                  amount={`£${formatCurrency(financeAmount)}`}
                  documents={educationSponsor}
                  financials={eduSponsorFinance}
                  birthCertificate={birthCertificate}
                  onResume={onResume}
                  onUploadClick={(sponsorType: string, docType: string | null, docKey: string, infoOnly: boolean) => {
                    openDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      docType,
                      docKey,
                      infoOnly,
                      sponsorType === 'birthCertificate' ? 'Birth Certificate' : eduSponsorFinance.loanName
                    );
                  }}
                />
              );
            });
          }
          case 'familySponsorship': {
            const familySponsorMap = documents[key] as FamilySponsorshipDocMap;
            return Object.entries(familySponsorMap).map(([famKey, familySponsor]) => {
              const familySponsorFinance = financials.familySponsorship?.[famKey];
              if (!familySponsorFinance) {
                return null;
              }
              if (familySponsorFinance.accountType === 'Other') {
                return null; // these documents are managed in 'Invalid Funding' section
              }

              let birthCertificate: BirthCertificateDocs | undefined = undefined;
              if (sponsorWithBirthCert === famKey) {
                birthCertificate = documents.birthCertificate;
              }

              const financeAmount = familySponsorFinance.sponsorshipAmount;
              return (
                <FamilySection
                  infoOnly={infoOnly}
                  key={famKey}
                  id={famKey}
                  amount={`£${formatCurrency(financeAmount)}`}
                  financials={familySponsorFinance}
                  documents={familySponsor}
                  birthCertificate={birthCertificate}
                  onResume={onResume}
                  onUploadClick={(
                    sponsorType: string,
                    docType: string | null,
                    docKey: string,
                    infoOnly: boolean,
                    modalTitle: string
                  ) => {
                    openDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      docType,
                      docKey,
                      infoOnly,
                      modalTitle
                    );
                  }}
                />
              );
            });
          }
          case 'deposit': {
            const deposit = documents[key] as DepositDoc;
            const amountLabel = financials.courseFeeDeposit ? `£${formatCurrency(financials.courseFeeDeposit)}` : '';
            return (
              <Deposit
                infoOnly={infoOnly}
                key={'despoit-doc'}
                id={'deposit'}
                amount={amountLabel || ''}
                document={deposit}
                onResume={onResume}
                onUploadClick={(docKey: string, infoOnly: boolean) => {
                  openDocumentModal(null, null, 'deposit', docKey, infoOnly, 'Deposit');
                }}
              />
            );
          }
          case 'scholarshipSponsorship': {
            const scholarshipSponsorMap = documents[key] as ScholarshipDocsMap;
            return Object.entries(scholarshipSponsorMap).map(([scholarKey, scholarSponsor]) => {
              const scholarshipSponsorFinance = financials.scholarshipSponsorship?.[scholarKey];
              if (!scholarshipSponsorFinance) {
                return null;
              }
              const amountLabel = sponsorFundLabel(scholarshipSponsorFinance);
              return (
                <ScholarshipSponsor
                  infoOnly={infoOnly}
                  key={scholarKey}
                  id={scholarKey}
                  amount={amountLabel || ''}
                  documents={scholarSponsor}
                  financials={scholarshipSponsorFinance}
                  onResume={onResume}
                  onUploadClick={(docKey: string, infoOnly: boolean) => {
                    openDocumentModal(
                      'scholarshipSponsorship',
                      scholarKey,
                      'documents',
                      docKey,
                      infoOnly,
                      `${scholarshipSponsorFinance.scholarshipName}`
                    );
                  }}
                />
              );
            });
          }
          case 'customRequest': {
            const customRequestDocs = documents[key] as CustomRequestMap;

            return (
              <CustomRequestDocument
                key="custom-request"
                infoOnly={infoOnly}
                documents={customRequestDocs}
                onResume={onResume}
                onClick={(sponsorKey: string, docKey: string, infoOnly: boolean) =>
                  openDocumentModal('customRequest', sponsorKey, null, docKey, infoOnly)
                }
              />
            );
          }
          case 'governmentSponsorship':
          case 'companySponsorship': {
            const organisationSponsorMap = documents[key] as OrganisationDocsMap;
            return Object.entries(organisationSponsorMap).map(([orgKey, orgSponsor]) => {
              const orgSponsorFinance = financials.organisationSponsorship?.[orgKey];
              if (!orgSponsorFinance) {
                return null;
              }
              const amountLabel = sponsorFundLabel(orgSponsorFinance);
              return (
                <GovernmentOrCompany
                  infoOnly={infoOnly}
                  key={orgKey}
                  id={orgKey}
                  amount={amountLabel || ''}
                  documents={orgSponsor}
                  financials={orgSponsorFinance}
                  onResume={onResume}
                  sectionLocked={isSectionLocked}
                  onUploadClick={(docKey: string, infoOnly: boolean) => {
                    openDocumentModal(
                      getOrgSponsorType(orgSponsorFinance.orgType),
                      orgKey,
                      null,
                      docKey,
                      infoOnly,
                      getOrgSponsorType(orgSponsorFinance.orgType) === 'governmentSponsorship'
                        ? 'Government Sponsorship'
                        : 'Company Sponsorship'
                    );
                  }}
                />
              );
            });
          }
          case 'evidenceOfIncome': {
            const evidenceOfIncomeDocs = documents[key] as EvidenceOfIncomeDocs;

            return (
              <EvidenceOfIncomeSection
                infoOnly={infoOnly}
                key={'evidence-of-income-section'}
                id={'evidence-of-income-section'}
                financials={financials}
                documents={evidenceOfIncomeDocs}
                onResume={onResume}
                onUploadClick={(
                  sponsorType: string,
                  docType: string | null,
                  docKey: string,
                  infoOnly: boolean,
                  modalTitle: string
                ) => {
                  openDocumentModal(sponsorType, null, docType, docKey, infoOnly, modalTitle);
                }}
              />
            );
          }
          default:
            return null;
        }
      });
  };

  const getModal = () => {
    if (openModal?.isUpload) {
      return <FileUploadLocal openModal={openModal} />;
    }

    switch (openModal?.sponsorType) {
      case 'birthCertificate': {
        switch (openModal.docType) {
          case 'certificate': {
            const docKey = openModal.docKey;
            const doc: BirthCertificateDoc = documents.birthCertificate.certificate[docKey];

            const { fileNames } = doc;

            return (
              <BirthCertificateModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status,
                    infoOnlyRequirementsOption,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                onContinue={closeModal}
                onClose={closeModal}
                document={doc}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'birthCertificate',
                    null,
                    'certificate',
                    docKey,
                    'Birth Certificate',
                    fileNames,
                    metadata
                  )
                }
                onAddRequiredDocument={(metadata?: any) =>
                  onAddRequiredDocument('birthCertificate', null, 'certificate', docKey, 'translation', metadata)
                }
              />
            );
          }
          case 'translation': {
            const docKey = openModal.docKey;
            const doc: IDocument = documents.birthCertificate.translation[docKey];

            const { status, fileNames } = doc;

            return (
              <BirthCertificateTranslationModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={status !== 'AWAITING'}
                fileNames={fileNames}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'birthCertificate',
                    null,
                    'translation',
                    docKey,
                    'Birth Certificate Translation',
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          default: {
            return null;
          }
        }
      }
      case 'familySponsorship': {
        const sponsorKey = openModal.sponsorKey;
        const docKey = openModal.docKey;
        const familySponsorshipRec = documents.familySponsorship[sponsorKey];
        const familySponsor = financials.familySponsorship[sponsorKey];
        switch (openModal.docType) {
          case 'bankLetter': {
            const doc: BankLetterDoc = familySponsorshipRec.bankLetter[docKey];
            const { status, fileNames } = doc;
            const docName = bankLetterName(familySponsor.sponsorType);

            return (
              <BankLetterModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                sponsorType={familySponsor.sponsorType}
                accountOwner={familySponsor.accountType}
                fileNames={fileNames}
                sectionTitle={
                  familySponsor.accountType === 'Mother'
                    ? `Mother's ${docName}`
                    : familySponsor.accountType === 'Father'
                    ? `Father's ${docName}`
                    : familySponsor.accountType === 'Guardian'
                    ? `Guardian's ${docName}`
                    : `${docName}`
                }
                onContinue={closeModal}
                document={doc}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'bankLetter',
                    docKey,
                    `${docName}`,
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'parentLetter': {
            const doc: ParentLetterDoc = familySponsorshipRec.parentLetter[docKey];
            const { fileNames } = doc;

            return (
              <ParentLetterModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status,
                    infoOnlyRequirementsOption,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                fileNames={fileNames}
                onContinue={closeModal}
                document={doc}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'parentLetter',
                    docKey,
                    'Parent Letter',
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'bankStatementTranslation': {
            const doc: IDocument = familySponsorshipRec.bankStatementTranslation[docKey];
            const { status, fileNames } = doc;
            return (
              <BankStatementTranslationModal
                sponsorType={familySponsor.sponsorType}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={status !== 'AWAITING'}
                fileNames={fileNames}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'bankStatementTranslation',
                    docKey,
                    bankStatementTransName(familySponsor.sponsorType),
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'bankStatement': {
            const doc: BankStatementDoc = familySponsorshipRec.bankStatement[docKey];
            const { fileNames } = doc;

            const docName = bankStatementName(familySponsor.sponsorType);
            const sectionTitle =
              familySponsor.accountType === 'Mother'
                ? `Mother's ${docName}`
                : familySponsor.accountType === 'Father'
                ? `Father's ${docName}`
                : familySponsor.accountType === 'Guardian'
                ? `Guardian's ${docName}`
                : familySponsor.accountType === 'Spouse'
                ? `Partner's ${docName}`
                : `Your ${docName}`;
            const sponsorshipAmount = familySponsor.sponsorshipAmount;
            return (
              <BankStatementModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status,
                    infoOnlyRequirementsOption,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                sponsorType={familySponsor.sponsorType}
                accountOwner={familySponsor.accountType}
                fileNames={fileNames}
                sectionTitle={sectionTitle}
                sponsorAmount={sponsorshipAmount}
                onContinue={closeModal}
                document={doc}
                onAddRequiredDocument={(newDocType: string) =>
                  onAddRequiredDocument('familySponsorship', sponsorKey, 'bankStatement', docKey, newDocType)
                }
                onUpload={(metadata?: any) => {
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'bankStatement',
                    docKey,
                    `${docName}`,
                    fileNames,
                    metadata
                  );
                }}
                onClose={closeModal}
              />
            );
          }
          case 'verificationBankLetter': {
            const doc = familySponsorshipRec.verificationBankLetter[docKey];
            const { status, fileNames } = doc;

            const nameToken =
              familySponsor.accountType === 'Mother'
                ? "Mother's"
                : familySponsor.accountType === 'Father'
                ? "Father's"
                : familySponsor.accountType === 'Guardian'
                ? "Guardian's"
                : 'your';

            return (
              <VerificationBankLetterModal
                sectionTitle={`Verification Letter from ${nameToken} Bank`}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={status !== 'AWAITING'}
                fileNames={fileNames}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'verificationBankLetter',
                    docKey,
                    'Verification Letter from Bank',
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          case 'secondaryBankStatement': {
            const doc = familySponsorshipRec.secondaryBankStatement[docKey];
            const { status, fileNames } = doc;

            return (
              <SecondaryBankStatementModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={doc.status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                complete={status !== 'AWAITING'}
                fileNames={fileNames}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'familySponsorship',
                    sponsorKey,
                    'secondaryBankStatement',
                    docKey,
                    'Secondary Bank Statement Scan',
                    fileNames,
                    metadata
                  )
                }
                onClose={closeModal}
              />
            );
          }
          default: {
            return null;
          }
        }
      }
      case 'companySponsorship':
      case 'governmentSponsorship': {
        const { sponsorType, sponsorKey, docType, docKey } = openModal;
        const orgDoc: Organisation = documents[sponsorType][sponsorKey];
        const doc: OrganisationDoc = orgDoc[docKey];

        const { status, fileNames } = doc;
        const orgSponsor = financials.organisationSponsorship[sponsorKey];

        const sectionTitle =
          getOrgSponsorType(orgSponsor.orgType) === 'companySponsorship' ? 'Company Sponsor' : 'Government Sponsor';
        return (
          <OrganisationSponsorModal
            infoOnly={!!openModal.isInfoOnly}
            docStatus={status}
            onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
              onAcknowledgeToggle(
                openModal.sponsorType,
                openModal.sponsorKey,
                openModal.docType,
                openModal.docKey,
                status,
                infoOnlyRequirementsOption,
                metadata
              )
            }
            docKey={openModal.docKey}
            fileNames={fileNames}
            sectionTitle={sectionTitle}
            onContinue={closeModal}
            document={doc}
            onUpload={(metadata?: any) =>
              openUploadFinanceModal(
                getOrgSponsorType(orgSponsor.orgType),
                sponsorKey,
                docType,
                docKey,
                sectionTitle,
                doc.fileNames,
                metadata
              )
            }
            onClose={closeModal}
          />
        );
      }
      case 'scholarshipSponsorship': {
        const { sponsorType, sponsorKey, docKey } = openModal;
        const scholarshipDoc: Scholarship = documents[sponsorType][sponsorKey];

        const doc: ScholarshipDoc = scholarshipDoc[docKey];
        const { fileNames } = doc;
        const scholarshipSponsor = financials.scholarshipSponsorship[sponsorKey];

        const sectionTitle = scholarshipSponsor.scholarshipName;

        return (
          <ScholarshipSponsorModal
            infoOnly={!!openModal.isInfoOnly}
            docStatus={doc.status}
            onAcknowledgeToggle={(status: string) =>
              onAcknowledgeToggle(openModal.sponsorType, sponsorKey, null, openModal.docKey, status)
            }
            docKey={openModal.docKey}
            fileNames={fileNames}
            sectionTitle={sectionTitle}
            onContinue={closeModal}
            document={doc}
            onUpload={(metadata?: any) =>
              openUploadFinanceModal(
                'scholarshipSponsorship',
                sponsorKey,
                null,
                docKey,
                sectionTitle,
                doc.fileNames,
                metadata
              )
            }
            onSaveMetaDataForNoDoc={(metadata?: any) => {
              onSaveMetaDataForNoDoc('scholarshipSponsorship', sponsorKey, null, docKey, metadata);
            }}
            onClose={closeModal}
          />
        );
      }
      case 'educationSponsorship': {
        const { sponsorType, sponsorKey, docType, docKey } = openModal;
        const educationDoc: Education = documents[sponsorType][sponsorKey];
        const doc: EducationDoc = educationDoc[docKey];
        const { fileNames } = doc;
        const educationSponsor = financials.educationSponsorship[sponsorKey];

        const { loanName, loanType } = educationSponsor;
        return (
          <EducationSponsorModal
            infoOnly={!!openModal.isInfoOnly}
            docStatus={doc.status}
            onAcknowledgeToggle={(status: string, infoOnlyRequirementsOption?: string, metadata?: any) =>
              onAcknowledgeToggle(
                openModal.sponsorType,
                openModal.sponsorKey,
                openModal.docType,
                openModal.docKey,
                status,
                infoOnlyRequirementsOption,
                metadata
              )
            }
            docKey={openModal.docKey}
            loanType={loanType}
            loanName={loanName}
            fileNames={fileNames}
            sectionTitle={'Education loan'}
            onContinue={closeModal}
            document={doc}
            onUpload={(metadata?: any) =>
              openUploadFinanceModal(
                'educationSponsorship',
                sponsorKey,
                null,
                docKey,
                'Education Loan',
                fileNames,
                metadata
              )
            }
            onClose={closeModal}
          />
        );
      }
      case 'customRequest': {
        const { sponsorType, sponsorKey, docType, docKey } = openModal;
        const customReqDoc: CustomRequest = documents[sponsorType][sponsorKey];
        const doc = customReqDoc[docKey];
        const { status, fileNames } = doc;

        return (
          <CustomRequestModal
            sponsorKey={sponsorKey}
            complete={['UPLOADED_OK', 'APPROVED'].includes(status)}
            fileNames={fileNames}
            sectionTitle={'Custom Document'}
            label={doc.label}
            description={doc.desc}
            onContinue={closeModal}
            document={doc}
            onUpload={() =>
              openUploadFinanceModal('customRequest', sponsorKey, docType, docKey, 'Custom request document', fileNames)
            }
            onClose={closeModal}
          />
        );
      }
      case 'incomeEvidence': {
        const leadSponsor = getLeadSponsorByKey(financials);

        const dispLeadSponsor = leadSponsor === 'Student' ? 'My' : `${leadSponsor}'s`;

        let isSupportingEvidenceProvided = false; // if so then question becomes locked

        const docKey = openModal.docKey;
        const docNumber = docKey.substr(docKey.lastIndexOf('-') + 1);
        const evidenceOfIncome = documents.evidenceOfIncome;
        const employmentReferenceDoc: IEvidenceOfIncomeDoc =
          evidenceOfIncome.employmentReference[`employment-reference-${docNumber}`];
        const recentEmploymentDoc: IEvidenceOfIncomeDoc =
          evidenceOfIncome.recentEmploymentContract[`employment-contact-${docNumber}`];
        const recentPayslipDoc: IEvidenceOfIncomeDoc =
          evidenceOfIncome.recentPayslip[`employment-payslip-${docNumber}`];
        const CVDoc: IDocument = evidenceOfIncome.curriculumVitae[`curriculum-vitae-${docNumber}`];

        const currentEmploymentStatus =
          employmentReferenceDoc?.currentEmploymentStatus ??
          recentEmploymentDoc?.currentEmploymentStatus ??
          recentPayslipDoc?.currentEmploymentStatus;

        const fundSource =
          employmentReferenceDoc?.fundSource ?? recentEmploymentDoc?.fundSource ?? recentPayslipDoc?.fundSource;

        const hasDocumentBeenUploaded =
          !['AWAITING', 'SKIPPED'].includes(employmentReferenceDoc?.status) ||
          !['AWAITING', 'SKIPPED'].includes(recentEmploymentDoc?.status) ||
          !['AWAITING', 'SKIPPED'].includes(recentPayslipDoc?.status);

        if ((fundSource ?? '') !== '' || hasDocumentBeenUploaded) {
          isSupportingEvidenceProvided = true;
        }
        switch (openModal.docType) {
          case 'curriculumVitae': {
            const sectionTitle = `${dispLeadSponsor} CV / Resume`;
            const { fileNames } = CVDoc;

            return (
              <CurriculumVitaeModal
                sectionTitle={sectionTitle}
                fileNames={fileNames}
                complete={CVDoc.status !== 'AWAITING'}
                onClose={closeModal}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'evidenceOfIncome',
                    null,
                    'curriculumVitae',
                    docKey,
                    sectionTitle,
                    fileNames,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={CVDoc.status}
                leadSponsor={leadSponsor.toLowerCase()}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                onSaveMetaDataForNoDoc={(metadata?: any) => {
                  onSaveMetaDataForNoDoc('incomeEvidence', null, 'curriculumVitae', docKey, metadata);
                }}
              />
            );
          }
          case 'employmentReference': {
            const sectionTitle = `${dispLeadSponsor} Employment Reference`;
            const { fileNames } = employmentReferenceDoc;

            return (
              <EmploymentReferenceModal
                isSupportingEvidenceProvided={isSupportingEvidenceProvided}
                sectionTitle={sectionTitle}
                fileNames={fileNames}
                complete={employmentReferenceDoc.status !== 'AWAITING'}
                onClose={closeModal}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'evidenceOfIncome',
                    null,
                    'employmentReference',
                    docKey,
                    sectionTitle,
                    fileNames,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={employmentReferenceDoc.status}
                leadSponsor={leadSponsor.toLowerCase()}
                currentEmploymentStatus={currentEmploymentStatus}
                fundSource={fundSource}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                onSaveMetaDataForNoDoc={(metadata?: any) => {
                  console.log('hjere');
                  onSaveMetaDataForNoDoc('incomeEvidence', null, 'employmentReference', docKey, metadata);
                }}
              />
            );
          }
          case 'recentEmploymentContract': {
            const sectionTitle = `${dispLeadSponsor} Employment Contract`;
            const { fileNames } = recentEmploymentDoc;

            return (
              <RecentEmploymentContractModal
                isSupportingEvidenceProvided={isSupportingEvidenceProvided}
                sectionTitle={sectionTitle}
                fileNames={fileNames}
                complete={recentEmploymentDoc.status !== 'AWAITING'}
                onClose={closeModal}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'evidenceOfIncome',
                    null,
                    'recentEmploymentContract',
                    docKey,
                    sectionTitle,
                    fileNames,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={recentEmploymentDoc.status}
                leadSponsor={leadSponsor.toLowerCase()}
                currentEmploymentStatus={currentEmploymentStatus}
                fundSource={fundSource}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                onSaveMetaDataForNoDoc={(metadata?: any) => {
                  onSaveMetaDataForNoDoc('evidenceOfIncome', null, 'recentEmploymentContract', docKey, metadata);
                }}
              />
            );
          }
          case 'recentPayslip': {
            const sectionTitle = `${dispLeadSponsor} Employment Payslip`;
            const { fileNames } = recentPayslipDoc;

            return (
              <RecentPayslipModal
                isSupportingEvidenceProvided={isSupportingEvidenceProvided}
                sectionTitle={sectionTitle}
                fileNames={fileNames}
                complete={recentPayslipDoc.status !== 'AWAITING'}
                onClose={closeModal}
                onContinue={closeModal}
                onUpload={(metadata?: any) =>
                  openUploadFinanceModal(
                    'evidenceOfIncome',
                    null,
                    'recentPayslip',
                    docKey,
                    sectionTitle,
                    fileNames,
                    metadata
                  )
                }
                docKey={openModal.docKey}
                infoOnly={!!openModal.isInfoOnly}
                docStatus={recentPayslipDoc.status}
                leadSponsor={leadSponsor.toLowerCase()}
                currentEmploymentStatus={currentEmploymentStatus}
                fundSource={fundSource}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                onSaveMetaDataForNoDoc={(metadata?: any) => {
                  onSaveMetaDataForNoDoc('evidenceOfIncome', null, 'recentPayslip', docKey, metadata);
                }}
              />
            );
          }
          default: {
            return null;
          }
        }
      }
      case 'invalidFunding': {
        return (
          <InvalidFundingModal
            isBankAccepted={true}
            onContinue={() => {
              closeModal();
              onResume('/finances/step-1');
            }}
            onClose={closeModal}
          />
        );
      }

      case 'awaitingDeposit': {
        return (
          <AwaitingDepositModal
            onContinue={() => {
              closeModal();
              onResume('/finances/step-1');
            }}
            onClose={closeModal}
            depositInstructionLink={depositInstructionLink}
            depositeUneditable={config.depositUneditable}
          />
        );
      }

      default: {
        switch (openModal?.docType) {
          case 'deposit': {
            const { docType, docKey } = openModal;
            const doc: DepositDoc = documents[docType][docKey];
            const { status, fileNames } = doc;
            return (
              <DepositModal
                infoOnly={!!openModal.isInfoOnly}
                docStatus={status}
                onAcknowledgeToggle={(status: string) =>
                  onAcknowledgeToggle(
                    openModal.sponsorType,
                    openModal.sponsorKey,
                    openModal.docType,
                    openModal.docKey,
                    status
                  )
                }
                docKey={openModal.docKey}
                fileNames={fileNames}
                sectionTitle={'Deposit'}
                onContinue={closeModal}
                document={doc}
                hasCopyOfDepositReceipt={doc.hasCopyOfDepositReceipt}
                onSaveMetaDataForNoDoc={(metadata?: any) => {
                  onSaveMetaDataForNoDoc(null, null, 'deposit', docKey, metadata);
                }}
                onUpload={(metadata?: any) => {
                  openUploadFinanceModal(null, null, 'deposit', docKey, 'Deposit', fileNames, metadata);
                }}
                onClose={closeModal}
              />
            );
          }
        }
        return null;
      }
    }
  };

  let missingFinancePlanAmountLabel: string | React.ReactNode = `£${formatCurrency(
    financials?.fundingSummary?.totalInvalid || 0
  )}`;
  if ((financials?.fundingSummary?.totalInvalid ?? 0) <= 0) {
    missingFinancePlanAmountLabel = 'None';
  }

  const summaryReplaced = summaryText.replace(
    '#TotalFundingToShow',
    `<b data-selector="total-funding-to-show">£${formatCurrency(totalFundingToShow)}</b>`
  );

  return (
    <>
      <Helmet>
        <title>
          {title} {titleAppend}
        </title>
      </Helmet>

      {getModal()}

      <PageHeader dataSelector="Dashboard-Header">{title}</PageHeader>

      <PageSubtext>
        {/* TODO: can we use something other than dangerouslySetInnerHTML? */}
        <span dangerouslySetInnerHTML={{ __html: isSectionLocked ? LOCKED_SECTION_SUMMARY : summaryReplaced }}></span>

        {!isSectionLocked && !isCASChecklistSectionLocked && (
          <Button
            data-selector="go-to-finance-plan-action"
            onClick={() => onResume('/finances/step-1')}
            mt={3}
            // onKeyPress={(evt: any) => (evt.key === 'Enter' ? onResume('/finances/step-1') : false)}
          >
            Add to/change finance plan
          </Button>
        )}
      </PageSubtext>

      <>
        {getInvalidFunding()}
        {getDepositRequest()}
        {summary.finDocsRequired > 0 || hasRequiredDocs || hasInfoOnlyDocs ? (
          <>
            {hasRequiredDocs ? (
              <>
                <SectionHeader title="Please upload the following:" />

                {renderDocs()}
              </>
            ) : null}
            {hasInfoOnlyDocs ? (
              <>
                <SectionHeader
                  title="Please review the following:"
                  subtext="These documents do not need to be uploaded but may be requested from you during your visa
                    application."
                  data-selector={'info-only-docs-section'}
                />

                {renderDocs(true)}
              </>
            ) : null}

            <Alert
              status={(financials?.fundingSummary?.totalInvalid ?? 0) > 0 ? 'warning' : 'info'}
              colorScheme={(financials?.fundingSummary?.totalInvalid ?? 0) > 0 ? 'orange' : 'gray'}
              mt={8}
              borderRadius="lg"
            >
              <AlertIcon />

              <Box>
                <AlertTitle>Amount missing from Finance Plan</AlertTitle>

                <AlertDescription data-selector="fin-docs-amount-to-prove">
                  {missingFinancePlanAmountLabel}
                </AlertDescription>

                {!isSectionLocked && !isCASChecklistSectionLocked && (
                  <Box>
                    <Button data-selector="change-finance-plan-action" onClick={() => onResume('/finances/step-1')}>
                      Add to/change finance plan
                    </Button>
                  </Box>
                )}
              </Box>
            </Alert>
          </>
        ) : (
          <NoDocuments />
        )}
      </>
    </>
  );
};

export default FinanceDocumentDashboard;
